import { api, ensureCsrfCookie } from '@/api/client';
import type {
  LoginPayload,
  LoginResponse,
  Organization,
  RegisterPayload,
  RegisterResponse,
  SessionResponse,
} from '@/types/api';

/**
 * Authentication calls.
 *
 * Every state-changing call primes the CSRF cookie first. Sanctum rejects a
 * write without it with a 419, which is easy to misread as an auth failure.
 */

export async function login(payload: LoginPayload): Promise<LoginResponse> {
  await ensureCsrfCookie();
  const { data } = await api.post<LoginResponse>('/auth/login', payload);
  return data;
}

export async function register(payload: RegisterPayload): Promise<RegisterResponse> {
  await ensureCsrfCookie();
  const { data } = await api.post<RegisterResponse>('/auth/register', payload);
  return data;
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout');
}

/** The session bootstrap: user, memberships, and the active organisation. */
export async function fetchSession(): Promise<SessionResponse> {
  const { data } = await api.get<SessionResponse>('/auth/me');
  return data;
}

export async function switchOrganization(
  organizationId: string,
): Promise<{ active_organization: Organization }> {
  const { data } = await api.post<{ active_organization: Organization }>(
    '/auth/switch-organization',
    { organization_id: organizationId },
  );
  return data;
}

export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ message: string }>('/auth/forgot-password', { email });
  return data;
}

export async function resetPassword(payload: {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}): Promise<{ message: string }> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ message: string }>('/auth/reset-password', payload);
  return data;
}
